import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment-timezone';
import { message } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { useAuthContext } from 'contexts';
import { useIsAdmin } from 'hooks/useIsAdmin';
import {
  DeskConfirmationType,
  useCancelProfileEmployeeVisitsAtLocationMutation,
  useConfirmDeskReservationForProfileMutation,
  useEndProfileReservationOrVisitsInRangeMutation,
  useEndOrCancelDelegatedProfileReservationMutation,
  useEndOrCancelProfileReservationMutation,
  useGetProfileCancelReservationsQuery,
  useGetProfileCancelSeriesInstancesQuery,
  useUpdateProfileRecurringReservationRdateMutation,
} from 'generated/graphql';
import {
  CancelOption,
  CancelReservationModal,
} from '../components/CancelReservationModal';
import { EditDeskSeriesModal } from '../components/EditDeskSeriesModal';
import { ReleaseDeskModal } from '../components/ReleaseDeskModal';
import {
  ProfileReservation,
  ReservationMenuAction,
  isReservationInProgress,
} from '../utils/getReservationActions';

type ProfileModal =
  | { type: 'release'; reservation: ProfileReservation }
  | { type: 'cancel'; reservation: ProfileReservation }
  | { type: 'editSeries'; reservation: ProfileReservation };

type UseProfileDeskReservationActionsArgs = {
  userId: string;
  isProfileSidebarOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
  onNavigateAway?: () => void;
  /**
   * The user's weekly-schedule recurrence per assigned desk id (from
   * GetProfileDeskSchedules). Drives the release modal's recurrence copy.
   */
  recurrenceByDeskId?: Record<string, string | null>;
};

type UseProfileDeskReservationActions = {
  isCurrentPerson: boolean;
  /** The viewer holds the org-wide admin role and may terminate anyone's reservation. */
  isAdmin: boolean;
  onCardClick: (reservation: ProfileReservation) => void;
  onCheckIn: (reservation: ProfileReservation) => void;
  onMenuAction: (
    action: ReservationMenuAction,
    reservation: ProfileReservation
  ) => void;
  /** The release/cancel modals, wired to the current pending action. */
  modals: React.ReactNode;
};

// robin-api's SchedulingError reason for confirming an already-confirmed
// reservation. The gateway's apiResolverGuard forwards `meta.errors` from the
// API response as `extensions.apiErrors`, so this is the same field the
// gateway's confirmDeskReservation resolvers inspect before swallowing it.
const EVENT_ALREADY_CONFIRMED_REASON = 'event_already_confirmed';

const isAlreadyConfirmedError = (error: unknown): boolean => {
  if (!(error instanceof ApolloError)) {
    return false;
  }
  // Key off the structured API reason only; message text is not a contract.
  return error.graphQLErrors.some((graphQLError) => {
    const apiErrors = graphQLError.extensions?.apiErrors;
    return (
      Array.isArray(apiErrors) &&
      apiErrors.some(
        (apiError: unknown) =>
          typeof apiError === 'object' &&
          apiError !== null &&
          (apiError as { reason?: unknown }).reason ===
            EVENT_ALREADY_CONFIRMED_REASON
      )
    );
  });
};

/**
 * The reservation action + modal logic shared by the profile sidebar's
 * bookings list (sider) and the fullscreen Desks panel. Both surfaces present
 * the same desk cards with the same check-in / edit / cancel / release /
 * unassign behavior; only the data window and layout differ, so that lives in
 * the callers while everything below is shared here.
 */
export const useProfileDeskReservationActions = ({
  userId,
  isProfileSidebarOpen = true,
  onModalOpenChange,
  onNavigateAway,
  recurrenceByDeskId = {},
}: UseProfileDeskReservationActionsArgs): UseProfileDeskReservationActions => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings',
  });
  const { currentOrg, currentUser, orgSlug } = useAuthContext();
  const navigate = useNavigate();
  // Contextual message API (not the static `message`) so toasts render under
  // the profile's ConfigProvider (shellOverlayTheme sets zIndexPopupBase to the
  // shell overlay z-index) and appear above the drawer — including in
  // fullscreen, where the static toast would be hidden behind it.
  const [messageApi, messageContextHolder] = message.useMessage();

  const [profileModal, setProfileModal] = useState<ProfileModal | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const hasOpenProfileModal = isProfileSidebarOpen && isProfileModalOpen;
  const releaseModalReservation =
    profileModal?.type === 'release' ? profileModal.reservation : null;
  const cancelModalReservation =
    profileModal?.type === 'cancel' ? profileModal.reservation : null;
  const editSeriesModalReservation =
    profileModal?.type === 'editSeries' ? profileModal.reservation : null;
  const previousUserId = useRef(userId);

  const openProfileModal = useCallback(
    (modal: ProfileModal) => {
      setProfileModal(modal);
      setIsProfileModalOpen(true);
      onModalOpenChange?.(true);
    },
    [onModalOpenChange]
  );

  const closeProfileModal = useCallback(() => {
    setIsProfileModalOpen(false);
  }, []);

  const clearProfileModal = useCallback(() => {
    setIsProfileModalOpen(false);
    setProfileModal(null);
    onModalOpenChange?.(false);
  }, [onModalOpenChange]);

  useEffect(() => {
    const userChanged = previousUserId.current !== userId;
    previousUserId.current = userId;

    if (isProfileSidebarOpen && !userChanged) {
      return;
    }

    clearProfileModal();
  }, [clearProfileModal, isProfileSidebarOpen, userId]);

  const isCurrentPerson = currentUser?.id === userId;
  const { isAdmin } = useIsAdmin();

  // Deliberately not awaiting the refetch: once the mutation succeeds the
  // check-in is real, and a failed refetch must not surface as a failed
  // check-in (the card catches up when the refetch lands).
  const [confirmDesk] = useConfirmDeskReservationForProfileMutation({
    refetchQueries: ['GetProfileBookings'],
  });
  const refetchProfileBookingQueries = [
    'GetProfileBookings',
    'GetProfileDeskSchedules',
    'GetProfileSeriesInstances',
  ];
  const [endOrCancelReservation, { loading: endingOrCanceling }] =
    useEndOrCancelProfileReservationMutation({
      refetchQueries: refetchProfileBookingQueries,
      awaitRefetchQueries: true,
    });
  const [
    endOrCancelDelegatedReservation,
    { loading: endingOrCancelingDelegated },
  ] = useEndOrCancelDelegatedProfileReservationMutation({
    refetchQueries: refetchProfileBookingQueries,
    awaitRefetchQueries: true,
  });
  const [endReservationOrVisitsInRange, { loading: endingRange }] =
    useEndProfileReservationOrVisitsInRangeMutation({
      refetchQueries: refetchProfileBookingQueries,
      awaitRefetchQueries: true,
    });
  const [cancelEmployeeVisitsAtLocation, { loading: cancelingVisits }] =
    useCancelProfileEmployeeVisitsAtLocationMutation();
  const [updateSeriesRdate, { loading: updatingSeriesDates }] =
    useUpdateProfileRecurringReservationRdateMutation({
      refetchQueries: refetchProfileBookingQueries,
      awaitRefetchQueries: true,
    });

  const cancelReservationTimezone =
    cancelModalReservation?.timeZone ??
    cancelModalReservation?.seat.location.timezone ??
    'UTC';
  const cancelReservationStart = useMemo(
    () =>
      cancelModalReservation
        ? moment.tz(cancelModalReservation.startTime, cancelReservationTimezone)
        : null,
    [cancelModalReservation, cancelReservationTimezone]
  );
  const cancelReservationWeekRange = useMemo(() => {
    if (!cancelReservationStart) {
      return null;
    }

    const today = moment.tz(cancelReservationTimezone).startOf('minute');
    const startOfWeek = moment.tz(cancelReservationTimezone).startOf('week');
    const endOfWeek = moment.tz(cancelReservationTimezone).endOf('week');

    return cancelReservationStart.isBetween(startOfWeek, endOfWeek, 'day', '[]')
      ? { start: today, end: endOfWeek }
      : {
          start: cancelReservationStart.clone().startOf('week'),
          end: cancelReservationStart.clone().endOf('week'),
        };
  }, [cancelReservationStart, cancelReservationTimezone]);
  const cancelReservationUserId =
    cancelModalReservation?.reservee?.user?.id ?? userId;
  const cancelReservationReserverId =
    cancelModalReservation?.accountReserver?.id;
  const isCurrentUserReservee = currentUser?.id === cancelReservationUserId;
  const isCurrentUserReserver = currentUser?.id === cancelReservationReserverId;
  const isBookedByDelegate =
    !!cancelReservationUserId &&
    !!cancelReservationReserverId &&
    cancelReservationUserId !== cancelReservationReserverId;
  const isDelegatedCancel =
    !!currentUser?.id && !!cancelReservationUserId && !isCurrentUserReservee;
  const showDelegatedWarning =
    isDelegatedCancel && !isCurrentUserReserver && isBookedByDelegate;

  const { data: cancelReservationsData, loading: cancelReservationsLoading } =
    useGetProfileCancelReservationsQuery({
      skip: !cancelModalReservation || !cancelReservationWeekRange,
      variables: {
        userId: cancelReservationUserId,
        start: cancelReservationWeekRange?.start.toISOString() ?? '',
        end: cancelReservationWeekRange?.end.toISOString() ?? '',
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    });

  const cancelReservationDates = useMemo(
    () =>
      (cancelReservationsData?.getDeskReservationsByUserId.reservations ?? [])
        .map((reservation) =>
          moment.tz(reservation.startTime, cancelReservationTimezone)
        )
        .sort((left, right) => left.diff(right)),
    [cancelReservationTimezone, cancelReservationsData]
  );

  const cancelReservationSeriesId = cancelModalReservation?.seriesId ?? null;
  const { data: cancelSeriesData, loading: cancelSeriesLoading } =
    useGetProfileCancelSeriesInstancesQuery({
      skip: !cancelReservationSeriesId,
      variables: {
        seriesId: cancelReservationSeriesId ?? '',
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    });

  const cancelSeriesDates = useMemo(
    () =>
      [
        ...new Set(
          (cancelSeriesData?.getDeskReservationInstancesById ?? []).map(
            (instance) => instance.startTime
          )
        ),
      ]
        .map((startTime) => moment.tz(startTime, cancelReservationTimezone))
        .sort((left, right) => left.diff(right)),
    [cancelReservationTimezone, cancelSeriesData]
  );

  const handleCheckIn = useCallback(
    async (reservation: ProfileReservation) => {
      try {
        await confirmDesk({
          variables: {
            reservationId: reservation.id,
            type: DeskConfirmationType.Dashboard,
          },
        });
        void messageApi.success(t('checkInSuccess'));
      } catch (error) {
        // A reservation confirmed elsewhere (mobile, status board) is a
        // success, not a failure. The gateway usually maps
        // event_already_confirmed to a happy response; this guard covers the
        // paths that still surface it as an error.
        if (isAlreadyConfirmedError(error)) {
          void messageApi.success(t('checkInSuccess'));
          return;
        }
        void messageApi.error(t('checkInError'));
      }
    },
    [confirmDesk, messageApi, t]
  );

  const handleCardClick = useCallback(
    (reservation: ProfileReservation) => {
      if (!orgSlug) {
        return;
      }
      const { seat } = reservation;
      const params = new URLSearchParams();
      params.set('locations', seat.location.id);
      if (seat.level?.id) {
        params.set('levels', seat.level.id);
      }
      params.set('csr', JSON.stringify({ type: 'seats', id: seat.id }));
      params.set('startTime', reservation.startTime);
      navigate(`/${orgSlug}/officeV2?${params.toString()}`);
      onNavigateAway?.();
    },
    [navigate, orgSlug, onNavigateAway]
  );

  const handleEdit = useCallback(
    (reservation: ProfileReservation) => {
      // Series bookings edit their dates in place, like the office app's
      // desk details. Single bookings keep deep-linking into the office
      // app's edit mode (the availabilities query needs an integer user id,
      // so anything else falls through to the deep link too).
      if (
        reservation.seriesId &&
        Number.isInteger(Number(reservation.reservee?.user?.id))
      ) {
        openProfileModal({ type: 'editSeries', reservation });
        return;
      }
      if (!orgSlug) {
        return;
      }
      const { seat } = reservation;
      const params = new URLSearchParams();
      params.set('locations', seat.location.id);
      if (seat.level?.id) {
        params.set('levels', seat.level.id);
      }
      params.set('editReservation', reservation.id);
      params.set('start', reservation.startTime);
      navigate(`/${orgSlug}/officeV2?${params.toString()}`);
      onNavigateAway?.();
    },
    [navigate, orgSlug, onNavigateAway, openProfileModal]
  );

  const handleEditSeriesSave = useCallback(
    async (rdateRule: string) => {
      const seriesId = editSeriesModalReservation?.seriesId;
      if (!seriesId) {
        return;
      }

      try {
        await updateSeriesRdate({
          variables: {
            seriesId,
            rdateRule,
          },
        });
        void messageApi.success(t('editSeriesModal.success'));
        closeProfileModal();
      } catch {
        void messageApi.error(t('editSeriesModal.failure'));
      }
    },
    [
      closeProfileModal,
      editSeriesModalReservation,
      messageApi,
      t,
      updateSeriesRdate,
    ]
  );

  const handleReportIssue = useCallback(
    (reservation: ProfileReservation) => {
      if (!orgSlug) {
        return;
      }
      const { seat } = reservation;
      const params = new URLSearchParams();
      params.set('locations', seat.location.id);
      if (seat.level?.id) {
        params.set('levels', seat.level.id);
      }
      params.set('csr', JSON.stringify({ type: 'seats', id: seat.id }));
      params.set('sidebar', 'report-desk');
      navigate(`/${orgSlug}/officeV2?${params.toString()}`);
      onNavigateAway?.();
    },
    [navigate, orgSlug, onNavigateAway]
  );

  const handleCopyLink = useCallback(
    async (reservation: ProfileReservation) => {
      const shareUrl = process.env.REACT_APP_SHARE_URL;
      if (!shareUrl) {
        void messageApi.error(t('copyLinkError'));
        return;
      }
      try {
        await navigator.clipboard.writeText(
          `${shareUrl}/reservation/${reservation.id}`
        );
        void messageApi.success(t('copyLinkSuccess'));
      } catch {
        void messageApi.error(t('copyLinkError'));
      }
    },
    [messageApi, t]
  );

  const handleUnassign = useCallback(
    async (reservation: ProfileReservation) => {
      const reservationId = reservation.seriesId ?? reservation.id;

      try {
        if (isCurrentPerson) {
          await endOrCancelReservation({
            variables: {
              reservationId,
            },
          });
        } else {
          await endOrCancelDelegatedReservation({
            variables: {
              reservationId,
              notifyAssignee: true,
            },
          });
        }
        void messageApi.success(t('unassignSuccess'));
      } catch {
        void messageApi.error(t('unassignError'));
      }
    },
    [
      endOrCancelDelegatedReservation,
      endOrCancelReservation,
      isCurrentPerson,
      messageApi,
      t,
    ]
  );

  const handleCancel = useCallback(
    async (option: CancelOption, shouldCancelVisits: boolean) => {
      if (!cancelModalReservation || !cancelReservationStart) {
        return;
      }

      const reservationId = cancelModalReservation.id;

      try {
        if (option === 'multiple' && cancelModalReservation.seriesId) {
          // Cancelling the series master id ends the whole series in one
          // atomic robin-api operation (a fully-future series is cancelled
          // outright; an ongoing one has its recurrence truncated to now).
          // The range mutation fans out one upstream cancel per instance and
          // can partially fail, leaving orphaned instances behind a success
          // toast — and it cancels every reservation the user has in the
          // window, not just this series.
          if (isCurrentPerson) {
            await endOrCancelReservation({
              variables: {
                reservationId: cancelModalReservation.seriesId,
              },
            });
          } else {
            await endOrCancelDelegatedReservation({
              variables: {
                reservationId: cancelModalReservation.seriesId,
                notifyAssignee: true,
              },
            });
          }

          if (shouldCancelVisits && cancelReservationUserId) {
            // Visits are cancelled separately over the series' span with
            // cancelReservations: false — the reservation side was already
            // handled atomically above. The reservation cancel is the
            // primary action, so a visits failure gets its own message
            // instead of pretending everything worked.
            const visitsStart = cancelSeriesDates[0] ?? cancelReservationStart;
            const visitsEnd = (
              cancelSeriesDates[cancelSeriesDates.length - 1] ??
              cancelReservationStart
            )
              .clone()
              .add(1, 'day');

            try {
              await cancelEmployeeVisitsAtLocation({
                variables: {
                  employeeId: cancelReservationUserId,
                  locationId: cancelModalReservation.seat.location.id,
                  startTime: visitsStart.toISOString(),
                  endTime: visitsEnd.toISOString(),
                  cancelReservations: false,
                },
              });
            } catch {
              void messageApi.error(
                t('cancelReservationModal.visits_cancel_failure')
              );
              closeProfileModal();
              return;
            }
          }
        } else if (
          currentOrg &&
          cancelReservationWeekRange &&
          (option === 'multiple' || shouldCancelVisits)
        ) {
          const start =
            option === 'single'
              ? cancelReservationStart
              : cancelReservationWeekRange.start;
          const end =
            option === 'single'
              ? cancelReservationStart.clone().add(1, 'day')
              : cancelReservationWeekRange.end;

          await endReservationOrVisitsInRange({
            variables: {
              orgId: currentOrg.id,
              userId: cancelReservationUserId,
              start: start.toISOString(),
              end: end.toISOString(),
              cancelFutureVisits: shouldCancelVisits,
              creationType: ['MANUAL', 'AUTOMATIC'],
            },
          });
        } else if (isCurrentPerson) {
          await endOrCancelReservation({
            variables: {
              reservationId,
            },
          });
        } else {
          await endOrCancelDelegatedReservation({
            variables: {
              reservationId,
              notifyAssignee: true,
            },
          });
        }
        void messageApi.success(t('cancelReservationModal.cancel_success'));
        closeProfileModal();
      } catch {
        void messageApi.error(t('cancelReservationModal.cancel_failure'));
      }
    },
    [
      cancelEmployeeVisitsAtLocation,
      cancelModalReservation,
      cancelReservationStart,
      cancelReservationUserId,
      cancelReservationWeekRange,
      cancelSeriesDates,
      closeProfileModal,
      currentOrg,
      endReservationOrVisitsInRange,
      endOrCancelDelegatedReservation,
      endOrCancelReservation,
      isCurrentPerson,
      messageApi,
      t,
    ]
  );

  const handleMenuAction = useCallback(
    (action: ReservationMenuAction, reservation: ProfileReservation) => {
      if (action === 'copy') {
        void handleCopyLink(reservation);
        return;
      }
      if (action === 'edit') {
        handleEdit(reservation);
        return;
      }
      if (action === 'reportIssue') {
        handleReportIssue(reservation);
        return;
      }
      if (action === 'release') {
        openProfileModal({ type: 'release', reservation });
        return;
      }
      if (action === 'cancel') {
        openProfileModal({ type: 'cancel', reservation });
        return;
      }
      if (action === 'unassign') {
        void handleUnassign(reservation);
      }
    },
    [
      handleCopyLink,
      handleEdit,
      handleReportIssue,
      handleUnassign,
      openProfileModal,
    ]
  );

  const handleReleaseModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setProfileModal((currentModal) =>
          currentModal?.type === 'release' ? null : currentModal
        );
        onModalOpenChange?.(false);
      }
    },
    [onModalOpenChange]
  );

  const handleCancelModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setProfileModal((currentModal) =>
          currentModal?.type === 'cancel' ? null : currentModal
        );
        onModalOpenChange?.(false);
      }
    },
    [onModalOpenChange]
  );

  const handleEditSeriesModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setProfileModal((currentModal) =>
          currentModal?.type === 'editSeries' ? null : currentModal
        );
        onModalOpenChange?.(false);
      }
    },
    [onModalOpenChange]
  );

  const modals = (
    <>
      {messageContextHolder}
      <ReleaseDeskModal
        open={hasOpenProfileModal && profileModal?.type === 'release'}
        reservation={releaseModalReservation}
        recurrence={
          releaseModalReservation
            ? recurrenceByDeskId[releaseModalReservation.seat.id] ?? null
            : null
        }
        afterOpenChange={handleReleaseModalOpenChange}
        onClose={closeProfileModal}
        onSuccess={closeProfileModal}
      />
      {cancelModalReservation && (
        <CancelReservationModal
          open={hasOpenProfileModal && profileModal?.type === 'cancel'}
          afterOpenChange={handleCancelModalOpenChange}
          reservationId={cancelModalReservation.id}
          reservationSeriesId={cancelModalReservation.seriesId}
          reservationInProgress={isReservationInProgress(
            cancelModalReservation
          )}
          reservationStartDate={moment.tz(
            cancelModalReservation.startTime,
            cancelReservationTimezone
          )}
          reservationTimezone={cancelReservationTimezone}
          deskName={cancelModalReservation.seat.name}
          buildingName={cancelModalReservation.seat.location.name}
          floorName={cancelModalReservation.seat.level?.name}
          reservationUserId={cancelReservationUserId}
          reservationUserName={cancelModalReservation.reservee?.user?.name}
          reservationReserverName={cancelModalReservation.accountReserver?.name}
          reservationDates={cancelReservationDates}
          seriesDates={cancelSeriesDates}
          showDelegatedWarning={showDelegatedWarning}
          showMultipleOption={cancelReservationDates.length > 1}
          loading={
            cancelReservationsLoading ||
            cancelSeriesLoading ||
            endingRange ||
            endingOrCanceling ||
            endingOrCancelingDelegated ||
            cancelingVisits
          }
          onClose={closeProfileModal}
          onConfirm={handleCancel}
        />
      )}
      {editSeriesModalReservation && (
        <EditDeskSeriesModal
          open={hasOpenProfileModal && profileModal?.type === 'editSeries'}
          reservation={editSeriesModalReservation}
          saving={updatingSeriesDates}
          afterOpenChange={handleEditSeriesModalOpenChange}
          onClose={closeProfileModal}
          onSave={handleEditSeriesSave}
        />
      )}
    </>
  );

  return {
    isCurrentPerson,
    isAdmin,
    onCardClick: handleCardClick,
    onCheckIn: handleCheckIn,
    onMenuAction: handleMenuAction,
    modals,
  };
};
